/**
 * WhatPrank Hero Effects
 * ----------------------
 * Injects floating decorative elements into the hero section matching the
 * Lovable design: a mix of Lucide-style SVG icons (Package, Skull, Flame,
 * Laugh, Mail) and circular WhatPrank logo images, all at 12% opacity
 * with a 6s float animation and staggered delays.
 *
 * Depends on:
 *   - .wp-floating-icon class defined in whatprank-custom.css
 *   - whatprank-logo-white.png in theme assets
 */

(function () {
  'use strict';

  /* Lucide-style SVG icons (24×24 viewBox, stroke-based) */
  var iconSvgs = {
    package: '<path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>',
    skull: '<circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/><path d="M8 20v2h8v-2"/><path d="m12.5 17-.5-1-.5 1h1z"/><path d="M16 20a2 2 0 0 0 1.56-3.25 8 8 0 1 0-11.12 0A2 2 0 0 0 8 20"/>',
    flame: '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>',
    laugh: '<circle cx="12" cy="12" r="10"/><path d="M18 13a6 6 0 0 1-6 5 6 6 0 0 1-6-5h12Z"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>',
    mail: '<rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>'
  };

  /**
   * Floating items matching Lovable's exact floatingItems array:
   * 6 icons + 3 logos, positioned at hero edges.
   */
  var floatingItems = [
    { type: 'icon',  icon: 'package', top: '15%', left: '8%',   delay: 0,   size: 48 },
    { type: 'logo',                   top: '35%', left: '3%',   delay: 1.8, size: 44 },
    { type: 'icon',  icon: 'skull',   top: '60%', left: '5%',   delay: 1,   size: 36 },
    { type: 'icon',  icon: 'flame',   top: '25%', right: '10%', delay: 2,   size: 48 },
    { type: 'logo',                   top: '50%', right: '6%',  delay: 0.8, size: 40 },
    { type: 'icon',  icon: 'laugh',   top: '70%', right: '8%',  delay: 0.5, size: 40 },
    { type: 'icon',  icon: 'mail',    top: '45%', left: '12%',  delay: 3,   size: 32 },
    { type: 'logo',                   top: '78%', left: '10%',  delay: 2.5, size: 36 },
    { type: 'icon',  icon: 'package', top: '80%', right: '15%', delay: 1.5, size: 28 }
  ];

  /** Read the logo URL from the script tag's data-logo-url attribute,
   *  set by Liquid in theme.liquid for correct Shopify CDN path. */
  function getLogoUrl() {
    var script = document.querySelector('script[data-logo-url]');
    if (script) {
      return script.getAttribute('data-logo-url');
    }
    return '/assets/whatprank-logo-white.png';
  }

  /** Build an SVG element string for a given icon key and size. */
  function makeSvg(key, size) {
    var paths = iconSvgs[key] || iconSvgs.package;
    return '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + size +
      '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" ' +
      'stroke-linecap="round" stroke-linejoin="round">' + paths + '</svg>';
  }

  /** Inject floating elements into the first hero section. */
  function injectFloatingItems() {
    var hero = document.querySelector('.hero-wrapper:first-child .hero');
    if (!hero) return;

    var logoUrl = getLogoUrl();

    if (window.getComputedStyle(hero).position === 'static') {
      hero.style.position = 'relative';
    }

    floatingItems.forEach(function (item) {
      var el = document.createElement('div');
      el.className = 'wp-floating-icon';
      el.setAttribute('aria-hidden', 'true');

      el.style.top = item.top;
      if (item.left) el.style.left = item.left;
      if (item.right) el.style.right = item.right;
      el.style.animationDelay = item.delay + 's';

      if (item.type === 'logo') {
        /* Circular logo image */
        var img = document.createElement('img');
        img.src = logoUrl;
        img.alt = '';
        img.width = item.size;
        img.height = item.size;
        img.style.borderRadius = '50%';
        img.style.display = 'block';
        el.appendChild(img);
      } else {
        /* SVG icon */
        el.innerHTML = makeSvg(item.icon, item.size);
      }

      hero.appendChild(el);
    });
  }

  /**
   * Inject pulsing dot + lock icon into the urgency badge <p>.
   * Placed directly inside the paragraph's inline flow so they render
   * on the same line as the text, avoiding Shopify's spacing-style layout.
   */
  function injectUrgencyBadgeIcons() {
    var badge = document.querySelector('.hero .text-block--background.h6:not(:first-child) p');
    if (!badge) return;

    var isMobile = window.innerWidth <= 749;

    /* On mobile, shorten the text to fit a single compact line */
    if (isMobile) {
      /* Find the text node(s) inside the <p> and replace with short copy */
      badge.childNodes.forEach(function(node) {
        if (node.nodeType === 3 && node.textContent.trim().length > 0) {
          node.textContent = 'Limited dispatch — lock in your date now';
        }
      });
    }

    /* Pulsing yellow dot — smaller on mobile */
    var dotSize = isMobile ? '8px' : '10px';
    var dot = document.createElement('span');
    dot.setAttribute('aria-hidden', 'true');
    dot.style.cssText = 'display:inline-block;width:' + dotSize + ';height:' + dotSize +
      ';border-radius:50%;background:#FFD700;flex-shrink:0;animation:wp-pulse-dot 2s ease-in-out infinite;';

    /* Lock SVG icon — smaller on mobile */
    var lockSize = isMobile ? '12' : '14';
    var lock = document.createElement('span');
    lock.setAttribute('aria-hidden', 'true');
    lock.style.cssText = 'display:inline-flex;align-items:center;flex-shrink:0;';
    lock.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="' + lockSize + '" height="' + lockSize + '" ' +
      'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;">' +
      '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>' +
      '<path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';

    badge.prepend(lock);
    badge.prepend(dot);
  }

  /**
   * Inject target/crosshair icon into the primary CTA button.
   * Matches Lovable's <Target className="w-5 h-5" /> before "Send a Prank".
   */
  function injectCTAIcon() {
    var btn = document.querySelector('.hero .button[class*="hero_btn_primary"]');
    if (!btn) return;

    var icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    icon.style.cssText = 'display:inline-flex;align-items:center;flex-shrink:0;margin-right:4px;';
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" ' +
      'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round">' +
      '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/>' +
      '<circle cx="12" cy="12" r="2"/></svg>';

    btn.prepend(icon);
  }

  /**
   * Slam-in animation for H1 heading words — SEO-safe single <h1>.
   *
   * The Shopify customizer stores the hero headline as two separate text
   * blocks: hero_heading_1 (<h1> "SEND ANONYMOUS", white) and
   * hero_heading_2 (<p> "PRANK MAIL.", yellow). Visually correct but
   * semantically broken — Google only sees "SEND ANONYMOUS" as the H1.
   *
   * This function merges both blocks into a single <h1> so the full
   * phrase "SEND ANONYMOUS PRANK MAIL." lives in one heading element.
   * Each word is wrapped in a .wp-slam-word span with staggered --delay
   * for the slam-in animation. A <br> separates the two lines.
   */
  function slamHeadingWords() {
    var blocks = document.querySelectorAll('.hero .text-block.h1');
    if (!blocks.length) return;

    /* Collect text + color from each block before mutating the DOM */
    var lines = [];
    blocks.forEach(function (block) {
      var textEl = block.querySelector('p') || block.querySelector('h1') ||
                   block.querySelector('h2') || block.querySelector('h3');
      if (!textEl) return;
      var text = (textEl.textContent || '').trim();
      if (!text) return;
      /* Read the inline color set by the Shopify theme customizer.
         Falls back to white if no color is explicitly set. */
      var color = textEl.style.color ||
                  window.getComputedStyle(textEl).color || '#ffffff';
      lines.push({ text: text, color: color, el: textEl, block: block });
    });

    if (!lines.length) return;

    /* Use the first block's element as the single <h1>.
       If it's not already an <h1>, replace it with one. */
    var primary = lines[0];
    var h1;
    if (primary.el.tagName === 'H1') {
      h1 = primary.el;
    } else {
      h1 = document.createElement('h1');
      /* Copy over any inline styles from the original element */
      h1.style.cssText = primary.el.style.cssText;
      primary.el.parentNode.replaceChild(h1, primary.el);
    }
    h1.innerHTML = '';

    /* Build slam-word spans for every line, separated by <br> */
    var wordIndex = 0;
    lines.forEach(function (line, lineIdx) {
      /* Insert a <br> between lines (not before the first) */
      if (lineIdx > 0) {
        h1.appendChild(document.createElement('br'));
      }
      var words = line.text.split(/\s+/);
      words.forEach(function (word) {
        var span = document.createElement('span');
        span.className = 'wp-slam-word';
        span.textContent = word;
        span.style.color = line.color;
        span.style.setProperty('--delay', (0.1 + wordIndex * 0.07) + 's');
        h1.appendChild(span);
        wordIndex++;
      });
    });

    /* Remove subsequent blocks from the DOM — their content now
       lives inside the first block's <h1>. */
    for (var i = 1; i < lines.length; i++) {
      lines[i].block.parentNode.removeChild(lines[i].block);
    }
  }

  /**
   * Typewriter effect for the handwritten subheading.
   * Reveals characters one at a time (25ms each) after an initial 0.8s delay,
   * with a blinking pink cursor that disappears when typing finishes.
   * Matches Lovable's TypewriterSubline component.
   */
  function typewriterSubheading() {
    var el = document.querySelector('.hero .text-block.h4 p');
    if (!el) return;

    var fullText = el.textContent || '';
    if (!fullText.trim()) return;

    /* Preserve layout space with invisible full text + visible partial */
    var visible = document.createElement('span');
    var invisible = document.createElement('span');
    invisible.style.visibility = 'hidden';
    invisible.textContent = fullText;
    var cursor = document.createElement('span');
    cursor.className = 'wp-typewriter-cursor';

    el.innerHTML = '';
    el.appendChild(visible);
    el.appendChild(cursor);
    el.appendChild(invisible);

    var charIndex = 0;
    var CHAR_DELAY = 25;  /* ms per character, matching Lovable */
    var START_DELAY = 350; /* ms — starts typing as MAIL. slams in (slam starts ~310ms) */

    setTimeout(function type() {
      if (charIndex >= fullText.length) {
        /* Typing complete — remove cursor, trigger landing pulse */
        if (cursor.parentNode) cursor.parentNode.removeChild(cursor);
        if (invisible.parentNode) invisible.parentNode.removeChild(invisible);
        el.classList.add('wp-subhead-landed');
        return;
      }
      charIndex++;
      visible.textContent = fullText.slice(0, charIndex);
      invisible.textContent = fullText.slice(charIndex);
      setTimeout(type, CHAR_DELAY);
    }, START_DELAY);
  }

  /* =========================================================================
   * PHASE 5: FULL BLOWN PRANK EXPERIENCE
   * =========================================================================
   * Enhance the media-with-content "What's Inside" section: colorize heading,
   * inject Lucide icons into feature rows, add testimonial overlay on the
   * media image, and append the location badge below it.
   * ======================================================================= */

  /**
   * Lucide SVG icon paths for the 5 features.
   * Order matches Lovable: Package, Gift, FileText, PartyPopper, Sparkles.
   */
  var featureIcons = [
    /* Package */
    '<path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>',
    /* Gift */
    '<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>',
    /* FileText */
    '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>',
    /* PartyPopper */
    '<path d="M5.8 11.3 2 22l10.7-3.79"/><path d="M4 3h.01"/><path d="M22 8h.01"/><path d="M15 2h.01"/><path d="M22 20h.01"/><path d="m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10"/><path d="m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11c-.11.7-.72 1.22-1.43 1.22H17"/><path d="m11 2 .33.82c.34.86-.2 1.82-1.11 1.98C9.52 4.9 9 5.52 9 6.23V7"/><path d="M11 13c1.93 1.93 2.83 4.17 2 5-.83.83-3.07-.07-5-2-1.93-1.93-2.83-4.17-2-5 .83-.83 3.07.07 5 2Z"/>',
    /* Sparkles */
    '<path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/>'
  ];

  /**
   * Enhance the "What's Inside" / "Full Blown Prank Experience" section.
   * - Colorize "PRANK EXPERIENCE" in pink
   * - Wrap feature text-blocks in flex rows with icon containers
   * - Inject testimonial overlay on the media image
   * - Append location badge below the media
   */
  function injectWhatsInsideEnhancements() {
    var section = document.querySelector('.media-with-content.color-scheme-5');
    if (!section) return;

    /* --- Colorize "PRANK EXPERIENCE" in gold (standardized 3-tier header pattern) --- */
    var h2 = section.querySelector('.text-block.h2 h2');
    if (h2) {
      h2.innerHTML = h2.innerHTML.replace(
        'PRANK EXPERIENCE',
        '<span class="wp-text-gold">PRANK EXPERIENCE</span>'
      );
    }

    /* --- Wrap feature blocks with icon containers --- */
    var contentBlocks = section.querySelectorAll('.media-with-content__content .text-block.rte');
    /* Skip the first rte block (that's the description paragraph).
     * Feature blocks are the rte blocks that contain a <strong> tag. */
    var featureIndex = 0;
    contentBlocks.forEach(function (block) {
      var strongEl = block.querySelector('strong');
      if (!strongEl) return; /* Not a feature block */
      if (featureIndex >= featureIcons.length) return;

      /* Create the wrapper row */
      var row = document.createElement('div');
      row.className = 'wp-feature-row';

      /* Create the icon container */
      var iconBox = document.createElement('div');
      iconBox.className = 'wp-feature-icon';
      iconBox.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + featureIcons[featureIndex] + '</svg>';

      /* Wrap: insert row before block, move block inside, prepend icon */
      block.parentNode.insertBefore(row, block);
      row.appendChild(iconBox);
      row.appendChild(block);

      featureIndex++;
    });

    /* --- Inject mailer image into empty media block --- */
    var scriptTag = document.querySelector('script[data-mailer-url]');
    var mailerUrl = scriptTag ? scriptTag.getAttribute('data-mailer-url') : '';
    if (mailerUrl) {
      var mediaContainer = section.querySelector('.media-block__media');
      if (mediaContainer && !mediaContainer.querySelector('img')) {
        var img = document.createElement('img');
        img.src = mailerUrl;
        img.alt = 'WhatPrank realistic prank mailer package';
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:16px;';
        img.loading = 'lazy';
        mediaContainer.appendChild(img);
      }
    }

    /* --- Inject testimonial overlay on the media image --- */
    var mediaBlock = section.querySelector('.media-block');
    if (mediaBlock) {
      /* The .media-block__media selector matches the <img> tag directly,
       * which can't have child elements. Wrap it in a positioned div so
       * the absolute-positioned overlay renders on top of the image. */
      var imgEl = mediaBlock.querySelector('img.media-block__media') || mediaBlock.querySelector('img');
      if (imgEl) {
        var wrapper = document.createElement('div');
        wrapper.className = 'wp-media-wrapper';
        wrapper.style.position = 'relative';
        wrapper.style.overflow = 'hidden';
        wrapper.style.borderRadius = '16px';
        imgEl.parentNode.insertBefore(wrapper, imgEl);
        wrapper.appendChild(imgEl);

        var overlay = document.createElement('div');
        overlay.className = 'wp-testimonial-overlay';
        overlay.innerHTML =
          '<div class="wp-testimonial-overlay__quote">"THE PACKAGE <span class="wp-text-gold">GOT DELIVERED</span> TO MY COMMUNITY MAILBOX AND SAT THERE ALL WEEK. <span class="wp-text-gold">ALL MY NEIGHBORS SAW IT</span>. FML..."</div>' +
          '<div class="wp-testimonial-overlay__author">— Prank Victim, Secondhand Sex Toys Prank</div>';
        wrapper.appendChild(overlay);
      }

      /* --- Append location badge below the media --- */
      var badge = document.createElement('div');
      badge.className = 'wp-location-badge';
      badge.innerHTML =
        '<div class="wp-location-badge__label">SHIPS FROM OUR OFFICE IN</div>' +
        '<div class="wp-location-badge__city">BUFFALO, NY, USA 🦬</div>' +
        '<div class="wp-location-badge__info">Small business. In-house logistics. 3-7 day delivery time.<br>Scheduling of delivery date optional — <a href="/pages/schedule-delivery">learn more ›</a></div>';
      mediaBlock.appendChild(badge);
    }
  }

  /* =========================================================================
   * PRODUCT DETAIL PAGE (PDP) ENHANCEMENTS
   * =========================================================================
   * Injects: star rating + badge above title, gold "MAIL PRANK" accent,
   * free shipping label, add-on toggle pills, custom message/date fields,
   * ATC button text, trust badges, and shipping info note.
   * Only runs on /products/* pages.
   * ======================================================================= */
  function enhanceProductPage() {
    if (window.location.pathname.indexOf('/products/') === -1) return;

    /* Wait for Horizon's web components to render */
    var detailsBlock = document.querySelector('product-details, .group-block-content');
    if (!detailsBlock) return;

    /* --- 1. Star rating + badge above title --- */
    var titleBlock = document.querySelector('.text-block h1');
    if (titleBlock && !document.querySelector('.wp-pdp-rating-row')) {
      var titleWrapper = titleBlock.closest('.text-block');
      if (titleWrapper) {
        /* Build rating row — Judge.me preview badge replaces hardcoded stars.
           The jdgm-widget div is hydrated by Judge.me's script (via content_for_header).
           We grab the product ID from the URL-matched Shopify JSON. */
        var ratingRow = document.createElement('div');
        ratingRow.className = 'wp-pdp-rating-row';

        /* Judge.me preview badge — pulls live rating + count */
        var productId = '';
        var productJsonEl = document.querySelector('[data-product-id]');
        if (productJsonEl) {
          productId = productJsonEl.getAttribute('data-product-id') || '';
        } else {
          /* Fallback: grab from the review widget further down the page */
          var reviewWidget = document.querySelector('#judgeme_product_reviews');
          if (reviewWidget) productId = reviewWidget.getAttribute('data-id') || '';
        }

        /* Build the inline rating display.
           Reads from the Liquid data bridge (#wp-jdgm-data) which carries
           product.metafields.reviews.rating.value — available instantly. */
        var dataBridge = document.querySelector('#wp-jdgm-data');
        var ratingNum = dataBridge ? (dataBridge.getAttribute('data-rating') || '') : '';

        /* Helper: convert numeric rating to gold star characters */
        function buildStarsRow(num, row) {
          var r = parseFloat(num);
          var fs = Math.floor(r);
          var hh = (r - fs) >= 0.25;
          var sh = '';
          for (var i = 0; i < fs; i++) sh += '★';
          if (hh) sh += '½';
          var existingBadge = row.querySelector('.wp-pdp-badge');
          row.innerHTML =
            '<span class="wp-pdp-stars">' + sh + '</span>' +
            '<span class="wp-pdp-rating-text"><strong class="wp-pdp-rating-highlight">Rated ' + num + '/5</strong> by our happy customers</span>';
          if (existingBadge) row.appendChild(existingBadge);
        }

        if (ratingNum) {
          /* Instant: metafield data available from Liquid */
          buildStarsRow(ratingNum, ratingRow);
        } else {
          /* Fallback: poll the hydrated Judge.me review widget */
          ratingRow.innerHTML =
            '<span class="wp-pdp-stars">★★★★★</span>' +
            '<span class="wp-pdp-rating-text">by our happy customers</span>';

          var attempts = 0;
          var poller = setInterval(function() {
            attempts++;
            var w = document.querySelector('#judgeme_product_reviews');
            if (w) {
              var a = w.querySelector('.jdgm-rev-widg__average');
              if (a && a.textContent.trim()) {
                clearInterval(poller);
                buildStarsRow(a.textContent.trim(), document.querySelector('.wp-pdp-rating-row') || ratingRow);
                return;
              }
            }
            if (attempts >= 12) clearInterval(poller);
          }, 500);
        }

        titleWrapper.parentNode.insertBefore(ratingRow, titleWrapper);
      }
    }

    /* --- Badge overlay on product image --- */
    /* Inject a .wp-badge into .product-information__media so it overlays
       the carousel image, matching homepage/collection card badge style.
       Reads product tags from the variant-picker JSON to pick badge type. */
    var mediaContainer = document.querySelector('.product-information__media');
    if (mediaContainer && !mediaContainer.querySelector('.wp-badge')) {
      /* Determine badge from product tags embedded in variant JSON */
      var badgeText = '';
      var badgeClass = 'wp-badge--lime';
      var variantJson = document.querySelector('variant-picker script[type="application/json"]');
      if (variantJson) {
        try {
          var vData = JSON.parse(variantJson.textContent);
          /* The product object isn't directly here, but we can check the page
             for product tags via the Shopify JSON-LD or data attributes */
        } catch (e) { /* ignore parse errors */ }
      }
      /* Fallback: scan for tag data attributes or default to BEST SELLER.
         Horizon exposes product tags in structured data — grab from LD+JSON. */
      var ldJson = document.querySelector('script[type="application/ld+json"]');
      if (ldJson) {
        try {
          var ld = JSON.parse(ldJson.textContent);
          /* Shopify LD+JSON doesn't include tags, so we use a simpler heuristic:
             check compare_at_price for SALE, otherwise show BEST SELLER */
          if (ld && ld.offers) {
            var offer = Array.isArray(ld.offers) ? ld.offers[0] : ld.offers;
            /* If there's a strikethrough price visible, it's on sale */
            var compareEl = document.querySelector('.compare-at-price');
            if (compareEl && compareEl.textContent.trim()) {
              badgeText = 'SALE';
              badgeClass = 'wp-badge--pink';
            }
          }
        } catch (e) { /* ignore */ }
      }
      /* Default badge if none detected */
      if (!badgeText) {
        badgeText = 'BEST SELLER';
        badgeClass = 'wp-badge--lime';
      }

      var badgeEl = document.createElement('span');
      badgeEl.className = 'wp-badge ' + badgeClass;
      badgeEl.textContent = badgeText;
      mediaContainer.appendChild(badgeEl);
    }

    /* --- 2. Gold accent on "MAIL PRANK" — drop to second line, smaller font --- */
    if (titleBlock) {
      var titleText = titleBlock.textContent.trim();
      if (titleText.indexOf('Mail Prank') > -1) {
        var parts = titleText.split('Mail Prank');
        titleBlock.innerHTML =
          '<span class="wp-pdp-title-main">' + parts[0].trim() + '</span>' +
          '<span class="wp-pdp-title-accent">MAIL PRANK</span>';
      }
    }

    /* --- 3. Free Shipping label next to price --- */
    var priceEl = document.querySelector('product-price .price');
    if (priceEl && !document.querySelector('.wp-pdp-free-shipping')) {
      var priceContainer = priceEl.closest('[ref="priceContainer"]') || priceEl.parentNode;
      var freeShip = document.createElement('span');
      freeShip.className = 'wp-pdp-free-shipping';
      freeShip.textContent = 'Free Shipping';
      priceContainer.appendChild(freeShip);
    }

    /* --- 4. Rework add-ons to toggle pills --- */
    var variantPicker = document.querySelector('variant-picker');
    if (variantPicker && !document.querySelector('.wp-pdp-addon-pills')) {
      var fieldsets = variantPicker.querySelectorAll('fieldset.variant-option');
      if (fieldsets.length > 0) {
        /* Create header */
        var addonsHeader = document.createElement('div');
        addonsHeader.className = 'wp-pdp-addons-header';
        addonsHeader.textContent = 'OPTIONAL ADD-ONS';
        variantPicker.parentNode.insertBefore(addonsHeader, variantPicker);

        /* Create pill grid */
        var pillGrid = document.createElement('div');
        pillGrid.className = 'wp-pdp-addon-pills';

        fieldsets.forEach(function(fieldset) {
          var legend = fieldset.querySelector('legend');
          var legendText = legend ? legend.textContent.trim() : '';

          /* Find the "No" and "$5/$10" radio inputs */
          var noRadio = fieldset.querySelector('input[value="No"]');
          var addRadio = fieldset.querySelector('input:not([value="No"])');
          if (!noRadio || !addRadio) return;

          var addValue = addRadio.value; /* e.g. "$5" or "$10" */

          /* Create pill — compact label: "Item Name ($5)" */
          var pill = document.createElement('div');
          pill.className = 'wp-pdp-addon-pill';
          if (addRadio.checked) pill.classList.add('is-active');
          pill.innerHTML =
            '<span class="wp-pdp-addon-pill__label">' + legendText + ' (' + addValue + ')</span>';

          /* Toggle handler — clicks the underlying radio inputs */
          pill.addEventListener('click', function() {
            if (pill.classList.contains('is-active')) {
              /* Deselect: click the "No" radio */
              noRadio.click();
              pill.classList.remove('is-active');
            } else {
              /* Select: click the add-on radio */
              addRadio.click();
              pill.classList.add('is-active');
            }
          });

          pillGrid.appendChild(pill);
        });

        variantPicker.parentNode.insertBefore(pillGrid, variantPicker);
      }
    }

    /* --- 5. Custom message textarea --- */
    /* Must be INSIDE the <form> so properties[...] are submitted to cart */
    var productForm = document.querySelector('product-form-component form');
    var formButtons = productForm ? productForm.querySelector('.product-form-buttons') : null;
    if (productForm && formButtons && !document.querySelector('.wp-pdp-custom-message')) {
      var msgField = document.createElement('div');
      msgField.className = 'wp-pdp-field wp-pdp-custom-message';
      msgField.innerHTML =
        '<label for="wp-custom-message">Custom message for recipient? (optional)</label>' +
        '<textarea id="wp-custom-message" name="properties[Custom Message]" rows="3" ' +
        'placeholder="Write a personal message to include with the prank..."></textarea>';
      productForm.insertBefore(msgField, formButtons);
    }

    /* --- 6. Delivery date picker --- */
    if (productForm && formButtons && !document.querySelector('.wp-pdp-delivery-date')) {
      var dateField = document.createElement('div');
      dateField.className = 'wp-pdp-field wp-pdp-delivery-date';
      dateField.innerHTML =
        '<label for="wp-delivery-date">Specific delivery date? (optional)</label>' +
        '<input type="date" id="wp-delivery-date" name="properties[Delivery Date]">' +
        '<div class="wp-pdp-field__helper">We\'ll do our best to deliver on or near your requested date. Allow 3-7 business days.</div>';
      productForm.insertBefore(dateField, formButtons);
    }

    /* --- 7. ATC button text override --- */
    var atcTextSpan = document.querySelector('.add-to-cart-text__content > span:first-child');
    if (atcTextSpan && atcTextSpan.textContent.trim() === 'Add to cart') {
      atcTextSpan.textContent = 'SEND A PRANK →';
    }

    /* --- 8. Trust badges below ATC --- */
    var formComponent = document.querySelector('product-form-component');
    if (formComponent && !document.querySelector('.wp-pdp-trust-grid')) {
      var trustGrid = document.createElement('div');
      trustGrid.className = 'wp-pdp-trust-grid';
      trustGrid.innerHTML =
        '<div class="wp-pdp-trust-badge"><span class="wp-pdp-trust-badge__icon">🔒</span> 100% Anonymous</div>' +
        '<div class="wp-pdp-trust-badge"><span class="wp-pdp-trust-badge__icon">📦</span> Free Shipping</div>' +
        '<div class="wp-pdp-trust-badge"><span class="wp-pdp-trust-badge__icon">🚚</span> Ships in 24hrs</div>' +
        '<div class="wp-pdp-trust-badge"><span class="wp-pdp-trust-badge__icon">📋</span> Realistic Packaging</div>';
      formComponent.parentNode.insertBefore(trustGrid, formComponent.nextSibling);
    }

    /* --- 9. Shipping info note --- */
    if (formComponent && !document.querySelector('.wp-pdp-shipping-note')) {
      var trustGridEl = document.querySelector('.wp-pdp-trust-grid');
      var insertAfter = trustGridEl || formComponent;
      var note = document.createElement('div');
      note.className = 'wp-pdp-shipping-note';
      note.innerHTML =
        '<span class="wp-pdp-shipping-note__icon">📍</span>' +
        '<span class="wp-pdp-shipping-note__text">When checking out, enter your prank recipient\'s name and shipping address — that\'s where we\'ll send the package. Your identity stays completely anonymous.</span>';
      insertAfter.parentNode.insertBefore(note, insertAfter.nextSibling);
    }
  }

  /* ========================================================================
   * PDP — Extend two-column layout through FAQ + Reviews
   * ========================================================================
   * Moves FAQ and Reviews section content into the product details (right)
   * column so the sticky media gallery on the left has runway through the
   * entire page. "People Also Sent" stays full-width below as the break.
   * Only applies on desktop (>= 750px); mobile stacks naturally.
   * ===================================================================== */
  function extendProductColumns() {
    if (window.location.pathname.indexOf('/products/') === -1) return;
    if (window.innerWidth < 750) return;
    if (document.querySelector('.wp-pdp-faq--inlined')) return; /* already ran */

    /* Find the product-details container — this is Horizon's group-block flex
       column inside the right side of the grid. We append inside it and use
       CSS to ensure vertical stacking (flex-direction: column). */
    var detailsCol = document.querySelector('.product-details');
    if (!detailsCol) return;

    /* Mark the grid so CSS can make media sticky + details non-sticky */
    var productGrid = document.querySelector('.product-information__grid');
    if (productGrid) productGrid.classList.add('wp-pdp-grid--extended');

    /* --- Move FAQ section into the details column --- */
    var faqInner = document.querySelector('.wp-pdp-faq');
    var faqSection = faqInner ? faqInner.closest('section') : null;

    if (faqSection) {
      var faqWrapper = document.createElement('div');
      faqWrapper.className = 'wp-pdp-faq--inlined wp-pdp-inline-section';

      /* Move original nodes to preserve onclick handlers */
      var faqHeader = faqSection.querySelector('.wp-section-header');
      var faqAccordion = faqSection.querySelector('.wp-pdp-faq');
      if (faqHeader) faqWrapper.appendChild(faqHeader);
      if (faqAccordion) faqWrapper.appendChild(faqAccordion);

      detailsCol.appendChild(faqWrapper);

      /* Hide the now-empty FAQ section shell */
      faqSection.style.display = 'none';
    }

    /* --- Move Reviews section into the details column --- */
    var reviewsContainer = document.querySelector('.wp-product-reviews');
    var reviewsSection = reviewsContainer ? reviewsContainer.closest('section') || reviewsContainer : null;

    if (reviewsContainer) {
      var reviewsWrapper = document.createElement('div');
      reviewsWrapper.className = 'wp-pdp-reviews--inlined wp-pdp-inline-section';

      /* Move original DOM to preserve Judge.me event bindings */
      while (reviewsContainer.firstChild) {
        reviewsWrapper.appendChild(reviewsContainer.firstChild);
      }

      detailsCol.appendChild(reviewsWrapper);

      /* Hide the now-empty reviews section shell */
      if (reviewsSection) reviewsSection.style.display = 'none';
    }
  }

  function init() {
    /* Hero effects — homepage only (functions self-guard) */
    injectFloatingItems();
    injectUrgencyBadgeIcons();
    injectCTAIcon();
    slamHeadingWords();
    typewriterSubheading();

    /* What's Inside / Full Blown Prank Experience */
    injectWhatsInsideEnhancements();

    /* Product detail page enhancements */
    enhanceProductPage();

    /* Extend PDP two-column layout through FAQ + reviews */
    extendProductColumns();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
